import { DraftEmailType, EmailRequestType } from './emailEnums';
import { MessagingPlatform } from './messagingPlatform.enum';
import { RecipientType } from './userCommunication';

export type EmailProvider = MessagingPlatform.GMAIL | MessagingPlatform.OUTLOOK;

export interface ProviderAccount {
  provider: EmailProvider;
  accountId: string;
}

export interface UnifiedAttachment {
  name: string;
  contentType: string;
  contentBytes: string;
}

export interface UnifiedSendEmailRequest {
  providerAccount: ProviderAccount;
  reqType: EmailRequestType;
  subject?: string;
  body?: string;
  toAddresses?: string[];
  ccAddresses?: string[];
  bccAddresses?: string[];
  messageId?: string;
  draftId?: string;
  draftType?: DraftEmailType;
  additionalText?: string;
  attachments?: UnifiedAttachment[];
  attachmentsToRemove?: string[];
  modifiedQuotedContent?: string;
  // Opaque provider-native draft version (Gmail: the draft's underlying message id;
  // Outlook: the message's changeKey). Only checked for DRAFT/SEND_DRAFT requests against
  // an existing draftId. Omitted = no concurrency check, matching pre-existing behavior.
  expectedDraftVersion?: string;
}

export interface UnifiedSendEmailResponse {
  success: boolean;
  messageId: string;
  draftId?: string;
  messageRawText?: string;
  provider: EmailProvider;
  accountId: string;
  message: string;
  // Present on DRAFT save responses (both providers). Absent on SEND_DRAFT: once sent, the
  // draft no longer exists on Gmail, and Outlook's message moves out of the drafts state,
  // so there is nothing left to version.
  draftVersion?: string;
}

// Distinguishes a rejected write (someone else's newer draft is on the server) from any
// other failure, so the web can offer "reload the draft" instead of just showing an error.
export enum UnifiedEmailErrorCode {
  DRAFT_VERSION_CONFLICT = 'DRAFT_VERSION_CONFLICT',
}

export interface UnifiedSendEmailErrorResponse {
  success: false;
  errorCode: UnifiedEmailErrorCode;
  message: string;
  // The draft's current version on the provider, so the caller can decide whether to
  // reload-and-retry without a second round trip. Not always available.
  currentDraftVersion?: string;
}

export type EmailSearchLabel =
  | 'inbox'
  | 'sent'
  | 'drafts'
  | 'spam'
  | 'archive'
  | 'all'
  | 'updates'
  | 'forums'
  | 'promotions'
  | 'social';

export interface EmailRecipient {
  name: string;
  email: string;
  type: RecipientType;
}

export interface EmailSearchResult {
  messageId: string;
  threadMessageId?: string;
  subject?: string;
  author?: string;
  authorEmail?: string;
  date: number;
  snippet?: string;
  labelIds?: string[];
  isRead?: boolean;
  hasAttachments: boolean;
  label?: string;
  recipients?: EmailRecipient[];
  provider?: EmailProvider;
  accountId?: string;
}

export interface SearchEmailsRequest {
  from?: string;
  to?: string;
  subject?: string;
  keywords?: string;
  isRead?: boolean;
  hasAttachment?: boolean;
  labels?: EmailSearchLabel;
  after?: string;
  before?: string;
  maxResults?: number;
  pageTokens?: Record<EmailProvider, string>;
  larger?: string;
  smaller?: string;
}

export interface SearchEmailsResponse {
  messages: EmailSearchResult[];
  nextPageTokens?: Record<EmailProvider, string>;
  totalCount: number;
}

export interface GetEmailByIdRequest {
  messageId: string;
  provider: EmailProvider;
  accountId: string;
}

export interface GetEmailByIdResponse {
  email: EmailSearchResult;
  message: string;
}
